import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LayoutState, FeaturesConfiguration, DashboardStatus, CurrentUser, NavigationItem, SidebarMenuItem } from "./types";
import filter from "lodash/filter";
import orderBy from "lodash/orderBy";

const initialState: LayoutState = {
    generalSpinner: false,
   
    confirmationModal: {
        isOpen: false,
        message: "",
        title: "Confirmation",
    },

    docDownloadUrl: "",
    dashboardStatuses: [],
    jobStatuses: [],

    currentUser: null,
    logoUrl: null,

    navigation: [],
    sidebarMenu: [],

    isErrorModalOpen: false,
    errorModalMessage: null,
    errorModalDetailMessage: null,

    featuresConfiguration: {
        allowChangePassword: false,
        somEnabled: false,
        useSpotDefault: false,
        billingEnabled: false,
        otiEnabled: false,
        allowUserEdit: false,
        crossAccountMessagingEnabled: false,
        directoryDomain: '',
        isMasterAccount: false,
        renewbixEnabled: false,
        snapEnabled: false,
        staticReportsNewEnabled: false,
        unknownWarningsEnabled: false,
    },
};

const layoutSlice = createSlice({
    name: "layout",
    initialState,
    reducers: {
        setFeaturesConfiguration(state, action: PayloadAction<FeaturesConfiguration>) {
            state.featuresConfiguration = action.payload;
        },

        toggleGeneralSpinner(state, action: PayloadAction<boolean>) {
            state.generalSpinner = action.payload;
        },

        setNavigation(state, action: PayloadAction<NavigationItem[]>) {
            state.navigation = action.payload;
        },

        setMenu(state, action: PayloadAction<SidebarMenuItem[]>) {
            state.sidebarMenu = action.payload;
        },

        openErrorModal(state, action: PayloadAction<{ errorMessage: string; detailMessage?: string | null }>) {
            state.isErrorModalOpen = true;
            state.errorModalMessage = action.payload.errorMessage;
            state.errorModalDetailMessage = action.payload.detailMessage ?? null;
        },

        closeErrorModal(state) {
            state.isErrorModalOpen = false;
            state.errorModalMessage = null;
            state.errorModalDetailMessage = null;
        },

        showConfirmation(state, action: PayloadAction<Partial<Omit<LayoutState["confirmationModal"], "isOpen">>>) {
            state.confirmationModal.isOpen = true;
            state.confirmationModal.message = action.payload.message ?? '';
            state.confirmationModal.title = action.payload.title ?? "Confirmation";
        },

        hideConfirmation(state, _action: PayloadAction<boolean>) {
            state.confirmationModal.isOpen = false;
            state.confirmationModal.message = "";
            state.confirmationModal.title = "Confirmation";
        },

        hubNotificationJobs(state, action: PayloadAction<{ projectId: number; statuses?: DashboardStatus[] }>) {
            const newStatuses = filter(state.jobStatuses, (s) => s.projectId !== action.payload.projectId).concat(
                action.payload?.statuses ?? []
            );

            state.jobStatuses = orderBy(newStatuses, ["processUid"], ["asc"]);
        },

        hubDashboardLoadProgressUpdated(state, action: PayloadAction<{ statuses: DashboardStatus[] }>) {
            const processGuids = (action.payload.statuses ?? []).map((s) => s.processUid);
            const oldStatuses = [...state.dashboardStatuses];

            state.dashboardStatuses = orderBy(
                filter(oldStatuses, (s) => !processGuids.includes(s.processUid)).concat(action.payload.statuses ?? []),
                ["processUid"],
                ["asc"]
            );
        },

        cleanStatuses(state) {
            state.dashboardStatuses = filter(state.dashboardStatuses, (s) => s.processStatus !== 2);
        },

        dismissStatus(state, action: PayloadAction<string>) {
            state.dashboardStatuses = filter(state.dashboardStatuses, (s) => s.processUid !== action.payload);
        },

        setCurrentUser(state, action: PayloadAction<CurrentUser>) {
            state.currentUser = action.payload;
        },

        setLogUrl(state, action: PayloadAction<string | null>) {
            state.logoUrl = action.payload;
        },
    },
});

export const actions = layoutSlice.actions;
export default layoutSlice.reducer;